import type { ReactNode } from 'react';
import styled from 'styled-components';

export interface SectionLabelProps {
  children: ReactNode;
  /** Optional right-aligned action (e.g. a "View all" link). */
  action?: ReactNode;
  /**
   * Render the label as a heading element so it joins the screen-reader heading
   * outline. The accessible name is just the label text (e.g. "Trending").
   * Omit for a plain span when the label is not document structure.
   */
  as?: 'h2' | 'h3';
}

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

/* A section title, set in the body face at reading size.
   Was: uppercase mono at 0.14em tracking, in the accent, prefixed with a "// "
   code-comment sigil. Three things wrong with that — a social feed is not a
   terminal, all-caps tracked runs are the documented "kicker label" tell, and
   mono belongs to data (handles, counts, times, ids), not to chrome. The July
   RailGroup pass had already hidden the sigil on the home rail, which is the
   clearest signal it was costume. */
const Label = styled.span`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.color.textPrimary};
`;

/* A plain hairline. Was an accent→transparent gradient with a glow bloom under
   it: a gradient on a functional surface, and decoration pretending to be a
   divider. A rule divides; that is the whole job. */
const Rule = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.color.glassBorder};
  margin-bottom: 12px;
`;

/**
 * SectionLabel — a section title over a hairline. Renders two stacked block
 * children (label row + rule), so place it as a direct child of a block/column
 * container. Pass `as="h2"` (etc.) for section titles that should join the
 * heading outline. System/structure labels only — never user content.
 */
export function SectionLabel({ children, action, as }: SectionLabelProps) {
  return (
    <>
      <Row>
        <Label as={as}>{children}</Label>
        {action}
      </Row>
      <Rule aria-hidden="true" />
    </>
  );
}
